import React, { useState } from 'react';
import { Smartphone, Video, Calendar, FileText, FolderOpen, CheckCircle, Apple, Play, Sparkles, Star } from 'lucide-react';
import { appFeatures } from '../mockData';

const iconMap = {
  Video,
  Calendar,
  FileText,
  FolderOpen
};

const AppDownload = () => {
  const [hoveredButton, setHoveredButton] = useState(null);

  return (
    <section id="app" style={styles.section}>
      {/* Gradient Background Overlay */}
      <div style={styles.backgroundGradient}></div>
      
      {/* Floating Elements */}
      <div style={styles.floatingElements}>
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.floatingCircle,
              left: `${10 + i * 12}%`,
              top: `${20 + (i % 3) * 30}%`,
              width: `${30 + i * 10}px`,
              height: `${30 + i * 10}px`,
              animationDelay: `${i * 0.5}s`
            }}
          />
        ))}
      </div>

      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.textContent}>
            {/* Premium Badge */}
            <div style={styles.badgeWrapper}>
              <Sparkles size={16} style={{ color: '#FFB800' }} />
              <span style={styles.badge}>COMING SOON</span>
              <Star size={16} style={{ color: '#FFB800' }} />
            </div>

            {/* Title with Gradient */}
            <h2 style={styles.title}>
              Download Our <br />
              <span style={styles.titleGradient}>Mobile App</span>
            </h2>
            
            <p style={styles.subtitle}>
              Get instant access to healthcare services on the go. Book appointments,
              consult with doctors, and manage your health records all in one place.
            </p>

            {/* Enhanced Features List */}
            <div style={styles.features}>
              {appFeatures.map((feature, index) => {
                const Icon = iconMap[feature.icon];
                return (
                  <div 
                    key={feature.id} 
                    style={{
                      ...styles.feature,
                      animationDelay: `${index * 0.1}s`
                    }}
                    className="animate-fade-in-up"
                  >
                    <div style={styles.iconCircle}>
                      <CheckCircle size={24} style={{ color: '#28C08A' }} />
                    </div>
                    <div style={styles.featureContent}>
                      <div style={styles.featureTitle}>{feature.title}</div>
                      <div style={styles.featureDesc}>{feature.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Premium Store Buttons */}
            <div style={styles.storeButtons}>
              {/* App Store Button */}
              <button
                style={{
                  ...styles.storeBtn,
                  ...styles.appStoreBtn,
                  transform: hoveredButton === 'apple' ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
                  boxShadow: hoveredButton === 'apple' 
                    ? '0 16px 40px rgba(0, 0, 0, 0.15), 0 0 0 3px rgba(0, 0, 0, 0.05)'
                    : '0 8px 24px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={() => setHoveredButton('apple')}
                onMouseLeave={() => setHoveredButton(null)}
              >
                <div style={styles.storeIconWrapper}>
                  <Apple size={32} style={{ color: 'white' }} />
                </div>
                <div style={styles.storeBtnContent}>
                  <span style={styles.comingSoonWhite}>Coming Soon</span>
                  <span style={styles.storeNameWhite}>App Store</span>
                </div>
              </button>

              {/* Google Play Button */}
              <button
                style={{
                  ...styles.storeBtn,
                  ...styles.playStoreBtn,
                  transform: hoveredButton === 'google' ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
                  boxShadow: hoveredButton === 'google'
                    ? '0 16px 40px rgba(52, 168, 83, 0.3), 0 0 0 3px rgba(52, 168, 83, 0.1)'
                    : '0 8px 24px rgba(52, 168, 83, 0.15)'
                }}
                onMouseEnter={() => setHoveredButton('google')}
                onMouseLeave={() => setHoveredButton(null)}
              >
                <div style={styles.storeIconWrapper}>
                  <Play size={32} style={{ color: 'white', fill: 'white' }} />
                </div>
                <div style={styles.storeBtnContent}>
                  <span style={styles.comingSoonWhite}>Coming Soon</span>
                  <span style={styles.storeNameWhite}>Google Play</span>
                </div>
              </button>
            </div>

            {/* Stats Row */}
            <div style={styles.statsRow}>
              <div style={styles.statItem}>
                <div style={styles.statNumber}>4.8</div>
                <div style={styles.statLabel}>App Rating</div>
              </div>
              <div style={styles.statDivider}></div>
              <div style={styles.statItem}>
                <div style={styles.statNumber}>50K+</div>
                <div style={styles.statLabel}>Downloads</div>
              </div>
              <div style={styles.statDivider}></div>
              <div style={styles.statItem}>
                <div style={styles.statNumber}>24/7</div>
                <div style={styles.statLabel}>Support</div>
              </div>
            </div>
          </div>

          {/* Enhanced Phone Mockup */}
          <div style={styles.phoneWrapper}>
            <div style={styles.phoneGlow}></div>
            <div style={styles.phoneMockup}>
              {/* Phone Frame */}
              <div style={styles.phoneFrame}>
                {/* Notch */}
                <div style={styles.phoneNotch}></div>
                
                {/* Screen Content */}
                <div style={styles.phoneScreen}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ehealth-portal/artifacts/fae5ti3l_WhatsApp%20Image%202025-11-26%20at%2012.50.36_15ff8c52.jpg"
                    alt="GEWO Health App Preview"
                    style={styles.phoneImage}
                  />
                </div>

                {/* Home Button */}
                <div style={styles.homeButton}></div>
              </div>

              {/* Floating App Icons */}
              <div style={styles.floatingIcon1}>
                <Video size={20} style={{ color: '#286CFF' }} />
              </div>
              <div style={styles.floatingIcon2}>
                <Calendar size={20} style={{ color: '#28C08A' }} />
              </div>
              <div style={styles.floatingIcon3}>
                <FileText size={20} style={{ color: '#FFB800' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: '120px 0',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 50%, #FFFFFF 100%)',
    position: 'relative',
    overflow: 'hidden'
  },
  backgroundGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(40, 108, 255, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255, 184, 0, 0.05) 0%, transparent 50%)',
    pointerEvents: 'none'
  },
  floatingElements: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    pointerEvents: 'none'
  },
  floatingCircle: {
    position: 'absolute',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(40, 108, 255, 0.1), rgba(255, 184, 0, 0.1))',
    animation: 'float 15s infinite ease-in-out'
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 32px',
    position: 'relative',
    zIndex: 1
  },
  content: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center'
  },
  textContent: {
    maxWidth: '600px'
  },
  badgeWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #FFF9E6 0%, #FFEDD5 100%)',
    border: '2px solid #FFB800',
    borderRadius: '24px',
    padding: '10px 24px',
    marginBottom: '32px',
    boxShadow: '0 4px 12px rgba(255, 184, 0, 0.2)'
  },
  badge: {
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '1px',
    color: '#B8860B'
  },
  title: {
    fontSize: '56px',
    fontWeight: '900',
    lineHeight: '1.1',
    color: '#1A202C',
    marginBottom: '24px',
    letterSpacing: '-1px'
  },
  titleGradient: {
    background: 'linear-gradient(135deg, #286CFF 0%, #5B8BFF 50%, #FFB800 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  subtitle: {
    fontSize: '20px',
    color: '#64748B',
    lineHeight: '1.7',
    marginBottom: '48px'
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    marginBottom: '48px'
  },
  feature: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    opacity: 0,
    animation: 'fadeInUp 0.6s ease-out forwards'
  },
  iconCircle: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #E8F5F1 0%, #D4F4E8 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(40, 192, 138, 0.15)'
  },
  featureContent: {
    flex: 1
  },
  featureTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '6px'
  },
  featureDesc: {
    fontSize: '15px',
    color: '#64748B',
    lineHeight: '1.6'
  },
  storeButtons: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
    marginBottom: '48px'
  },
  storeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    borderRadius: '20px',
    padding: '18px 32px',
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    minWidth: '200px'
  },
  appStoreBtn: {
    background: 'linear-gradient(135deg, #000000 0%, #2C2C2C 100%)',
    color: 'white'
  },
  playStoreBtn: {
    background: 'linear-gradient(135deg, #34A853 0%, #2D8F47 100%)',
    color: 'white'
  },
  storeIconWrapper: {
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '12px',
    backdropFilter: 'blur(10px)'
  },
  storeBtnContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    textAlign: 'left'
  },
  comingSoonWhite: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.8)',
    fontWeight: '500'
  },
  storeNameWhite: {
    fontSize: '18px',
    fontWeight: '700',
    color: 'white',
    letterSpacing: '0.3px'
  },
  statsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '24px 0',
    border: '1px solid rgba(40, 108, 255, 0.1)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)'
  },
  statItem: {
    flex: 1,
    textAlign: 'center',
    padding: '0 20px'
  },
  statNumber: {
    fontSize: '32px',
    fontWeight: '800',
    background: 'linear-gradient(135deg, #286CFF, #FFB800)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '4px'
  },
  statLabel: {
    fontSize: '13px',
    color: '#64748B',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  statDivider: {
    width: '1px',
    height: '40px',
    background: 'rgba(40, 108, 255, 0.2)'
  },
  phoneWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    padding: '40px'
  },
  phoneGlow: {
    position: 'absolute',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(40, 108, 255, 0.15) 0%, transparent 70%)',
    filter: 'blur(40px)',
    animation: 'pulse 4s infinite ease-in-out'
  },
  phoneMockup: {
    position: 'relative',
    zIndex: 2,
    animation: 'float 6s infinite ease-in-out'
  },
  phoneFrame: {
    position: 'relative',
    width: '340px',
    height: '680px',
    background: 'linear-gradient(135deg, #1A1A1A 0%, #2C2C2C 100%)',
    borderRadius: '48px',
    padding: '12px',
    boxShadow: '0 30px 80px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 0 0 1px rgba(255, 255, 255, 0.05)',
    border: '2px solid rgba(255, 255, 255, 0.1)'
  },
  phoneNotch: {
    position: 'absolute',
    top: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '140px',
    height: '28px',
    background: '#000000',
    borderRadius: '0 0 20px 20px',
    zIndex: 10,
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
  },
  phoneScreen: {
    width: '100%',
    height: '100%',
    background: '#FFFFFF',
    borderRadius: '40px',
    overflow: 'hidden',
    position: 'relative'
  },
  phoneImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  homeButton: {
    position: 'absolute',
    bottom: '8px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '140px',
    height: '5px',
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '3px'
  },
  floatingIcon1: {
    position: 'absolute',
    top: '10%',
    right: '-20px',
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #E3EDFF, #F0F6FF)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(40, 108, 255, 0.2)',
    animation: 'float 5s infinite ease-in-out',
    animationDelay: '0s',
    border: '2px solid white'
  },
  floatingIcon2: {
    position: 'absolute',
    top: '40%',
    left: '-20px',
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #E8F5F1, #D4F4E8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(40, 192, 138, 0.2)',
    animation: 'float 5s infinite ease-in-out',
    animationDelay: '1s',
    border: '2px solid white'
  },
  floatingIcon3: {
    position: 'absolute',
    bottom: '15%',
    right: '-20px',
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #FFF9E6, #FFEDD5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(255, 184, 0, 0.2)',
    animation: 'float 5s infinite ease-in-out',
    animationDelay: '2s',
    border: '2px solid white'
  }
};

export default AppDownload;
